import { useEffect } from 'react';
import { AchievementWatcher } from './components/AchievementWatcher';
import { AppShell } from './components/AppShell';
import { Celebration } from './components/Celebration';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LinkHandler } from './components/LinkHandler';
import { ToastProvider } from './components/Toast';
import { usePerfMonitor } from './hooks/usePerfMonitor';
import { sounds } from './hooks/useSound';
import { GameProvider, useGame } from './state/GameContext';
import { NavProvider, useNav } from './state/NavContext';
import { ProfileProvider, useProfile } from './state/ProfileContext';
import { SettingsProvider, useSettings } from './state/SettingsContext';
import { SiteConfigProvider } from './state/SiteConfigContext';
import { StatsProvider } from './state/StatsContext';
import { SupporterProvider } from './state/SupporterContext';
import { AdminScreen } from './screens/AdminScreen';
import { HistoryScreen } from './screens/HistoryScreen';
import { HomeScreen } from './screens/HomeScreen';
import { HowToScreen } from './screens/HowToScreen';
import { OnboardingScreen } from './screens/OnboardingScreen';
import { PlayScreen } from './screens/PlayScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { StatsScreen } from './screens/StatsScreen';

function PerfOverlay() {
  const { settings } = useSettings();
  const perf = usePerfMonitor(settings.perfOverlay);
  if (!settings.perfOverlay) return null;
  return (
    <div className="cv-perf" aria-hidden="true">
      {perf.fps} fps · {perf.ms}ms
    </div>
  );
}

function Router() {
  const { route } = useNav();
  switch (route.screen) {
    case 'play':
      return <PlayScreen />;
    case 'stats':
      return <StatsScreen />;
    case 'history':
      return <HistoryScreen />;
    case 'settings':
      return <SettingsScreen />;
    case 'howto':
      return <HowToScreen />;
    case 'admin':
      return <AdminScreen />;
    case 'home':
    default:
      return <HomeScreen />;
  }
}

function CelebrationLayer() {
  const { celebrationNonce } = useGame();
  return <Celebration trigger={celebrationNonce} />;
}

function Shell() {
  const { profile } = useProfile();
  const { route } = useNav();

  // Unlock WebAudio on the first user gesture (autoplay policy).
  useEffect(() => {
    const unlock = () => sounds.resume();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // The owner's /#admin link works on a fresh device without inventing a profile first.
  if (!profile && route.screen !== 'admin') return <OnboardingScreen />;

  return (
    <>
      <AppShell>
        <Router />
      </AppShell>
      <CelebrationLayer />
      <PerfOverlay />
      <AchievementWatcher />
      {profile ? <LinkHandler /> : null}
    </>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <SettingsProvider>
        <SiteConfigProvider>
          <ToastProvider>
            <SupporterProvider>
              <ProfileProvider>
                <StatsProvider>
                  <GameProvider>
                    <NavProvider>
                      <Shell />
                    </NavProvider>
                  </GameProvider>
                </StatsProvider>
              </ProfileProvider>
            </SupporterProvider>
          </ToastProvider>
        </SiteConfigProvider>
      </SettingsProvider>
    </ErrorBoundary>
  );
}
